import React from 'react'

interface AppErrorBoundaryProps {
  children: React.ReactNode
}

interface AppErrorBoundaryState {
  // A boolean, not the thrown value: `throw null` / `throw 0` must still show the fallback.
  hasError: boolean
}

function ErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <p>Something went wrong.</p>
      <button onClick={resetError}>Try again</button>
    </div>
  )
}

/**
 * Top-level error boundary for the whole app. Renders a minimal fallback
 * (deliberately free of i18n/theme dependencies, since those providers may be
 * what failed) and lets the user retry by re-rendering the children.
 */
export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error('[app-error-boundary]', error, errorInfo.componentStack)
  }

  resetError = () => {
    this.setState({ hasError: false })
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback resetError={this.resetError} />
    }
    return this.props.children
  }
}
